import BaseUrl from "../../base-api";
import { appendDischargePdfToFormData } from "./dischargePdfValidation";
import AxiosInstance from "../Utility/AxiosInstance";

const STEP_SAVE_ENDPOINTS = [
  "/api/auth/registration/save-ppp",
  "/api/auth/registration/save-family",
  "/api/auth/registration/save-address",
  "/api/auth/registration/save-contact",
  "/api/auth/registration/save-other-details",
  "/api/auth/registration/save-service",
  "/api/auth/registration/save-experience",
];

const multipartHeaders = { headers: { "Content-Type": "multipart/form-data" } };

const locationCode = (value) => value?.code || value || "";

function appendExperienceItem(formData, prefix, exp) {
  if (exp.id) {
    formData.append(`${prefix}.id`, exp.id);
  }
  formData.append(`${prefix}.designation`, exp.designation ?? "");
  formData.append(`${prefix}.agencyName`, exp.agencyName ?? "");
  formData.append(`${prefix}.fromDate`, exp.fromDate ?? "");
  formData.append(`${prefix}.toDate`, exp.toDate ?? "");
  formData.append(`${prefix}.experienceType`, exp.experienceType ?? "");
  formData.append(`${prefix}.pf`, exp.pf ?? "");
  formData.append(`${prefix}.pfNumber`, exp.pfNumber ?? "");
  formData.append(`${prefix}.esi`, exp.esi ?? "");
  formData.append(`${prefix}.esiNumber`, exp.esiNumber ?? "");
  if (exp.existingDocId) {
    formData.append(`${prefix}.existingDocId`, exp.existingDocId);
  }
  if (exp.document instanceof File) {
    formData.append(`${prefix}.document`, exp.document);
  }
}

function buildCombinedExperiences(govExperiences = [], privateExperiences = []) {
  return [
    ...(govExperiences || []).map((exp) => ({ ...exp, experienceType: "GOVERNMENT" })),
    ...(privateExperiences || []).map((exp) => ({ ...exp, experienceType: "PRIVATE" })),
  ];
}

export function buildExperienceStepFormData(govExperiences = [], privateExperiences = []) {
  const submitData = new FormData();
  const combined = buildCombinedExperiences(govExperiences, privateExperiences);
  combined.forEach((exp, index) => {
    appendExperienceItem(submitData, `experiences[${index}]`, exp);
  });
  return submitData;
}

export function buildPppFormData({ pppId, memberId, verifiedMemberName }) {
  const submitData = new FormData();
  submitData.append("pppId", pppId ?? "");
  submitData.append("memberId", memberId ?? "");
  submitData.append("verifiedMemberName", verifiedMemberName ?? "");
  return submitData;
}

export function buildFamilyStepFormData(formData = {}) {
  const submitData = new FormData();
  submitData.append("fullName", formData.fullName ?? "");
  submitData.append("fatherName", formData.fatherName ?? "");
  submitData.append("motherName", formData.motherName ?? "");
  submitData.append("dob", formData.dob ?? "");
  submitData.append("gender", formData.gender ?? "");
  submitData.append("maritalStatus", formData.maritalStatus ?? "");
  submitData.append("sasDistrict", locationCode(formData.sasDistrict));
  return submitData;
}

export function buildAddressStepFormData(formData = {}) {
  const submitData = new FormData();
  submitData.append("state", locationCode(formData.state));
  submitData.append("district", locationCode(formData.district));
  submitData.append("tehsil", locationCode(formData.tehsil));
  submitData.append("village", formData.village ?? "");
  submitData.append("address", formData.address ?? "");
  submitData.append("pinCode", formData.pinCode ?? "");
  return submitData;
}

export function buildContactStepFormData(formData = {}) {
  const submitData = new FormData();
  submitData.append("email", formData.email ?? "");
  submitData.append("altMobile", formData.altMobile ?? "");
  return submitData;
}

export function buildOtherDetailsStepFormData(formData = {}) {
  const submitData = new FormData();
  submitData.append("benchmarkDisability", formData.benchmarkDisability ?? "");
  submitData.append("disabilityPercentage", formData.disabilityPercentage ?? "");
  submitData.append("incomeVerified", formData.incomeVerified ?? "");
  submitData.append("casteCategoryId", formData.casteCategoryId ?? "");
  submitData.append("casteCategoryOther", formData.casteCategoryOther ?? "");
  submitData.append("highestQualificationId", formData.qualificationId ?? "");
  submitData.append("qualificationOther", formData.qualificationOther ?? "");
  submitData.append("currentEngagementId", formData.engagementId ?? "");
  submitData.append("engagementOther", formData.engagementOther ?? "");
  submitData.append("totalAnnualFamilyIncome", formData.totalAnnualFamilyIncome ?? "");
  return submitData;
}

export function buildServiceStepFormData(formData = {}) {
  const submitData = new FormData();
  submitData.append("coveredUnderSocioEconomic", formData.coveredUnderSocioEconomic ?? "");
  submitData.append("hasLicense", formData.hasLicense ?? "");
  const licenses = (formData.licenses || []).filter((license) => license?.licenseType?.trim());
  licenses.forEach((license, index) => {
    if (license.id) {
      submitData.append(`licenses[${index}].id`, license.id);
    }
    submitData.append(`licenses[${index}].licenseType`, license.licenseType.trim());
  });
  submitData.append("ppoNo", formData.ppoNo ?? "");
  submitData.append("ppoDate", formData.ppoDate ?? "");
  submitData.append("forceNumber", formData.forceNumber ?? "");
  submitData.append("forceName", formData.forceName ?? "");
  submitData.append("service", formData.service ?? "");
  submitData.append("type", formData.type ?? "");
  submitData.append("rank", formData.rank ?? "");
  submitData.append("medicalCategory", formData.medicalCategory ?? "");
  submitData.append("dischargeDate", formData.dischargeDate ?? "");
  submitData.append("enrollmentDate", formData.enrollmentDate ?? "");
  submitData.append("dischargeBookNo", formData.dischargeBookNo ?? "");
  submitData.append("unitRetired", formData.unitRetired ?? "");
  appendDischargePdfToFormData(formData, submitData);
  return submitData;
}

export function buildRegistrationFormData({
  formData,
  govExperiences = [],
  privateExperiences = [],
  hasNok = "No",
  nokDetails = {},
}) {
  const submitData = new FormData();

  const pppData = buildPppFormData({
    pppId: formData.pppId,
    memberId: formData.memberId,
    verifiedMemberName: formData.verifiedMemberName,
  });
  for (const [key, value] of pppData.entries()) {
    submitData.append(key, value);
  }

  const familyData = buildFamilyStepFormData(formData);
  for (const [key, value] of familyData.entries()) {
    submitData.append(key, value);
  }

  const addressData = buildAddressStepFormData(formData);
  for (const [key, value] of addressData.entries()) {
    submitData.append(key, value);
  }

  const contactData = buildContactStepFormData(formData);
  for (const [key, value] of contactData.entries()) {
    submitData.append(key, value);
  }

  const otherData = buildOtherDetailsStepFormData(formData);
  for (const [key, value] of otherData.entries()) {
    submitData.append(key, value);
  }

  const serviceData = buildServiceStepFormData(formData);
  for (const [key, value] of serviceData.entries()) {
    submitData.append(key, value);
  }

  const experienceData = buildExperienceStepFormData(govExperiences, privateExperiences);
  for (const [key, value] of experienceData.entries()) {
    submitData.append(key, value);
  }

  submitData.append("hasNok", hasNok || "No");
  if (hasNok === "Yes" && nokDetails.nokName) {
    submitData.append("nokDetails.nokName", nokDetails.nokName);
    submitData.append("nokDetails.relation", nokDetails.relation);
    submitData.append("nokDetails.age", nokDetails.age);
  }

  return submitData;
}

function buildStepFormData(step, payload) {
  const { formData, govExperiences, privateExperiences } = payload;

  switch (step) {
    case 0:
      return buildPppFormData({
        pppId: formData?.pppId,
        memberId: formData?.memberId || payload.memberId,
        verifiedMemberName: payload.verifiedMemberName || formData?.verifiedMemberName,
      });
    case 1:
      return buildFamilyStepFormData(formData);
    case 2:
      return buildAddressStepFormData(formData);
    case 3:
      return buildContactStepFormData(formData);
    case 4:
      return buildOtherDetailsStepFormData(formData);
    case 5:
      return buildServiceStepFormData(formData);
    case 6:
      return buildExperienceStepFormData(govExperiences, privateExperiences);
    default:
      throw new Error("Invalid registration step");
  }
}

export function buildNokFormData({ hasNok, nokDetails = {} }) {
  const submitData = new FormData();
  submitData.append("hasNok", hasNok || "No");
  if (hasNok === "Yes" && nokDetails.nokName) {
    submitData.append("nokDetails.nokName", nokDetails.nokName);
    submitData.append("nokDetails.relation", nokDetails.relation);
    submitData.append("nokDetails.age", nokDetails.age);
  }
  return submitData;
}

export async function fetchUserRegistrationData() {
  return AxiosInstance.get(`${BaseUrl}/api/auth/fetch-user-data`);
}

export async function saveNokDetails(payload) {
  const submitData = buildNokFormData(payload);
  return AxiosInstance.post(`${BaseUrl}/api/auth/registration/save-nok`, submitData, multipartHeaders);
}

export async function savePppDetails({ pppId, memberId }) {
  const submitData = buildPppFormData({ pppId, memberId });
  return AxiosInstance.post(`${BaseUrl}/api/auth/registration/save-ppp`, submitData, multipartHeaders);
}

export async function saveRegistrationStep(step, payload) {
  const endpoint = STEP_SAVE_ENDPOINTS[step];
  if (!endpoint) {
    throw new Error("Invalid registration step");
  }

  const submitData = buildStepFormData(step, payload);
  return AxiosInstance.post(`${BaseUrl}${endpoint}`, submitData, multipartHeaders);
}
